import * as React from 'react';
import { STATE } from 'app/constants';
import * as navigate from 'app/area/shared/navigation';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { SpinLoader, OverlayComponent, withPrompt, PromptProps } from 'app/components';
import { withOptions, OptionsProps } from 'app/components/options-handler';
import { Paper, PromptConfirmation } from 'ui';
import { CodetableStickyHeader } from 'app/area/codetable/item/sticky-header/codetable-sticky-header';
import { CodetableSelectorProps } from 'app/area/codetable/types';
import { actionCreators } from './actions';
import { CodetableAdministrationState } from '../types-store';
import { codetableAdministrationTranslation } from '../constants';
import { selectors as commonSelectors, getCodetableMetadata } from '../selectors';
import { selectors as codeItemSelectors } from 'app/area/shared/codetable/item/selectors';
import {
  canDuplicateHierarchyCodetable,
  codeAccessConfiguration, handleCreateCodeItem, handleLoadCodeItem,
  readOnlyConfiguration, substanceUomSetType
} from 'app/area/codetable/item/codetable-item-helpers';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { validate, ValidationData } from 'app/area/codetable/item/validation/validate';
import { getUserLanguageTranslation } from 'app/area/codetable/item/actions-helpers';
import { metroLineConfiguration } from 'app/area/codetable/item/configuration';
import { RefObject } from 'react';
import { routeConstants } from 'app/routes/route-constants';
import { CodeEditSectionType, CodeScreenValidate, InitializeCodeItemPayload } from 'app/area/codetable/item/types';
import { getCodetableId } from 'app/area/shared/codetable/breadcrumbs';
import { CodeScreenRenderer } from 'app/area/codetable/item/screen-renderer/code-screen-renderer';
import { supportedScreens } from 'app/area/codetable/item/screen-renderer/supported-screens';
import { validationToBooleanValues } from 'app/area/administration/shared/helpers';
import { getInvalidSection } from 'app/components/metro-line/helpers';
import { WithCodetableAccess, WithInvalidSection } from 'app/components/metro-line/types';
import { scrollToTop } from 'app/infrastructure/platform/routing';
import { MergeCodeModal } from 'app/area/codetable/common/merge-code/merge-code-modal';
import { escapeUrl } from 'app/components/favourite-layout/favourite/common/utils';
import { DuplicateHierarchyModal } from 'app/area/codetable/item/duplicate-hierarchy/duplicate-hierarchy-modal';

export type CodetableItemComponentProps = DefaultContainerProps & OptionsProps & PromptProps &
  CodetableAdministrationState & CodetableSelectorProps & typeof actionCreators;

interface CodetableItemComponentState extends WithInvalidSection<CodeEditSectionType>, WithCodetableAccess {
  showTranslations: boolean;
}

export class CodetableItemComponent extends React.PureComponent<CodetableItemComponentProps, CodetableItemComponentState> {
  public static statePath: string = STATE.administrationCodetable;

  private detailsSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private visibilitySectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private uomConversionSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private translationsSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private unitOfMeasureDetailsSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private unitOfMeasureSetsSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private substanceLinksSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private typesRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private scalarValuesRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private conversionFactorsSectionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private riskMatrixConfigurationRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

  public static getDerivedStateFromProps(props: CodetableItemComponentProps, state: CodetableItemComponentState) {
    const codes = props?.access?.data?.codes;
    return { codes: (codes !== state.codes) ? codes : undefined };
  }

  constructor(props: CodetableItemComponentProps) {
    super(props);

    this.state = {
      codes: props?.access?.data?.codes,
      invalidSection: undefined,
      showTranslations: undefined
    };
  }

  public componentDidMount() {
    scrollToTop();
    !this.state.codes && this.props.clearCodesAccess();
    handleCreateCodeItem(this.getLoadCodeItemPayload(this.props));

    !!this.props.codeId && this.switchTranslationsToggle(true);
  }

  public componentDidUpdate(prevProps: CodetableItemComponentProps) {
    handleLoadCodeItem(this.getLoadCodeItemPayload(this.props), this.getLoadCodeItemPayload(prevProps));

    if (prevProps?.item?.validation !== this.props?.item?.validation && this.state.invalidSection === undefined) {
      this.updateInvalidSection();
      this.updateTranslationsToggle();
    }
  }

  public componentWillUnmount() {
    this.resetForm();
  }

  private getLoadCodeItemPayload = (props: CodetableItemComponentProps): InitializeCodeItemPayload => (props
    ? { ...props, codetableId: getCodetableId(props.breadcrumbs), actionType: props?.params?.actionType, locale: props.sharedState.locale }
    : undefined
  );

  private getScreenValidation = (): CodeScreenValidate => {
    const { metadata: { data: metadata } } = this.props;

    const emptyValidation = (_: ValidationData) => ({});

    if (!supportedScreens || supportedScreens.length === 0 || !metadata || !metadata.codetable
      || !isDefinedIncludingZero(metadata.codetable.id) || !metadata.formName) {
      return emptyValidation;
    }

    const screenToRender = supportedScreens.find(screen => screen.id === metadata.codetable.id && screen.name === metadata.formName);

    return screenToRender && !!screenToRender.validate ? screenToRender.validate : emptyValidation;
  };

  private switchTranslationsToggle = (showTranslations: boolean) => this.setState({ showTranslations });

  private save = (withDescendants: boolean) => {
    const { saveCodetableItem, updateValidation, metadata: { data: metadata }, breadcrumbs,
      access: { data: { codes: { readonlySubsets, edit: canEdit } = {} } = {} }, options,
      item: { codeItem, isUomExcluded, uomConversionFactors, validation }, item,
      translation, sharedState: { language }, params: { actionType } } = this.props;

    const codetableId = getCodetableId(breadcrumbs);

    this.updateInvalidSection();

    updateValidation(validate({
      codetableId, codeItem, uomConversionFactors, metadata, translation, validation, actionType,
      validateScreen: this.getScreenValidation(), options, item
    }));

    this.updateTranslationsToggle();

    saveCodetableItem({
      codetableId,
      breadcrumbs,
      actionType,
      isUomExcluded,
      metadata, options, mainLanguage: language, validationErrorMessage: translation.validation_error,
      saveSubsetsOnly: !!readonlySubsets && readonlySubsets.length > 0 && !canEdit,
      substanceUomSetType: substanceUomSetType(codetableId),
      isHierarchy: this.isHierarchy(),
      withDescendants
    });
  };

  private cancel = () => navigate.replace(routeConstants.codetableAdministrationFor(this.props.breadcrumbs));

  private updateInvalidSection = () => {
    const { metadata: { data: metadata }, breadcrumbs, item: { codeItem, validation }, params: { actionType } } = this.props;

    const codetableId = getCodetableId(breadcrumbs);

    const codetableMetadata = getCodetableMetadata(metadata, codetableId);

    const configuration = metroLineConfiguration({
      detailsSectionRef: this.detailsSectionRef, supportedScreens, scalarValuesRef: this.scalarValuesRef,
      visibilitySectionRef: this.visibilitySectionRef, translationsSectionRef: this.translationsSectionRef,
      uomConversionSectionRef: this.uomConversionSectionRef,
      unitOfMeasureSetsSectionRef: this.unitOfMeasureSetsSectionRef, unitOfMeasureDetailsSectionRef: this.unitOfMeasureDetailsSectionRef,
      typesRef: this.typesRef, breadcrumbs, substanceLinksSectionRef: this.substanceLinksSectionRef,
      conversionFactorsSectionRef: this.conversionFactorsSectionRef,
      riskMatrixConfigurationRef: this.riskMatrixConfigurationRef,
      codeId: codeItem?.id, metadata: codetableMetadata, validationErrors: validationToBooleanValues(validation), actionType
    });

    const invalidSection = getInvalidSection(configuration);

    this.setState({ invalidSection });
  };

  private updateTranslationsToggle = () => {
    const { item: { validation } } = this.props;

    if (!!validation?.language) {
      this.switchTranslationsToggle(true);
    }
  };

  private resetForm = () => {
    const { resetCodeItem, clearValidation, clearCodesAccess } = this.props;

    clearCodesAccess();
    clearValidation();
    resetCodeItem();
    this.hideDuplicateHierarchy();
  };

  private duplicateCodeItem = () => {
    const { codeId, breadcrumbs } = this.props;
    navigate.replace(escapeUrl(routeConstants.codetableAdministrationDuplicateCodeFor(breadcrumbs, codeId)));
    return;
  };

  private hideDuplicateHierarchy = () =>
    !!this.props.item.showDuplicateHierarchy && this.props.toggleDuplicateHierarchy();

  private deleteCodeItem = (uncheckSelectItem: boolean) => {
    const { item: { mergeCode: { reassignReferencesToCode: { value = undefined } = {} } = {} },
      deleteCodeItem, translation, codeId, breadcrumbs } = this.props;

    deleteCodeItem({
      breadcrumbs, codeId,
      ...(!uncheckSelectItem && { reassignReferencesToCode: value }),
      successMessage: translation.code_item_successfully_deleted,
      violationErrorMessage: translation.merge_code_violation_error
    });
  };

  private mergeCodeItem = () => {
    this.props.updateMergeCode({
      ...this.props.item.mergeCode,
      codetableId: getCodetableId(this.props.breadcrumbs),
      codeId: this.props.codeId,
      categoryId: this.props.item.codeItem?.category?.value,
      showMergeCode: !this.props.item.mergeCode?.showMergeCode
    });
  };

  private getCodeItemDisplayedName = (original: boolean = false): string => {
    const { codeId, item: {
      codeItem: { name, node: { description: currentNodeDescription } = {}, description: currentDescription } = {}
    }, sharedState: { language } = {} } = this.props;

    const nodeDescription = original ? name : currentNodeDescription;
    const userLanguageDescription = original ? name
      : currentDescription?.find(description => description.value === language.value)?.description;

    if (!isDefinedIncludingZero(codeId)) {
      return undefined;
    }

    if (!!nodeDescription) {
      return nodeDescription;
    }

    if (!!userLanguageDescription) {
      return userLanguageDescription;
    }

    return undefined;
  };

  private isHierarchy = () => getCodetableMetadata(this.props.metadata?.data, getCodetableId(this.props.breadcrumbs))?.features?.hierarchy;

  // eslint:disable:max-func-body-length
  public render() {

    const {
      item: {
        fetchingCodeId, fetchingCreateOptions, codeItem, originalCodeItem, savingCodetableItem, deletingCodeItem, validation } = {},
      item: { codeItem: { id: codeId, disabled: codeItemDisabled } = {} } = {},
      metadata: { data } = {},
      access: { fetchingCodes, fetchingCodetables, data: { codetables: { create: canCreate, administrationCentral } = {},
        codes: { edit: canEdit, move: canMove, readonlySubsets } = {} } = {} } = {},
      breadcrumbs, translation, sharedState: { language } = {}, params: { actionType } = {}
    } = this.props;

    if (fetchingCodetables !== undefined || fetchingCodes !== undefined || fetchingCodeId !== undefined || fetchingCreateOptions
      || !codeItem) {
      return <SpinLoader />;
    }

    const codetableId = getCodetableId(breadcrumbs);

    const codetableMetadata = getCodetableMetadata(data, codetableId);

    const overlayText = savingCodetableItem ? translation.saving_code : translation.deleting_code;

    if (!codetableMetadata) {
      return null;
    }

    const currentLongDescription = getUserLanguageTranslation(language.value, codetableMetadata, codeItem, 'longDescription')
      || codeItem?.node?.longDescription;

    const originalLongDescription = getUserLanguageTranslation(language.value, codetableMetadata, originalCodeItem, 'longDescription')
      || originalCodeItem?.node?.longDescription;

    const configuration = metroLineConfiguration({
      detailsSectionRef: this.detailsSectionRef, supportedScreens, scalarValuesRef: this.scalarValuesRef,
      visibilitySectionRef: this.visibilitySectionRef, translationsSectionRef: this.translationsSectionRef,
      uomConversionSectionRef: this.uomConversionSectionRef,
      unitOfMeasureDetailsSectionRef: this.unitOfMeasureDetailsSectionRef, substanceLinksSectionRef: this.substanceLinksSectionRef,
      unitOfMeasureSetsSectionRef: this.unitOfMeasureSetsSectionRef, typesRef: this.typesRef,
      conversionFactorsSectionRef: this.conversionFactorsSectionRef, breadcrumbs,
      riskMatrixConfigurationRef: this.riskMatrixConfigurationRef,
      codeId, metadata: codetableMetadata, validationErrors: validationToBooleanValues(validation), actionType
    });

    const scrollViewUrl = isDefinedIncludingZero(codeId)
      ? routeConstants.codetableAdministrationCodeFor(breadcrumbs, codeId, actionType)
      : routeConstants.codetableAdministrationAddCodeFor(breadcrumbs);

    const initialSection = this.props.section as CodeEditSectionType;
    const readOnlyConfig = readOnlyConfiguration({
      actionType, codeId, administrationCentral, canCreate, canMove, canEdit, readonlySubsets, codetableMetadata
    });

    const access = codeAccessConfiguration(codetableId, this.props.access.data, undefined);

    const displayMergeCodeModal = !!this.props.item.mergeCode?.showMergeCode && codetableMetadata?.codetable?.id && codeItem.id;

    const canDuplicateHierarchy = canDuplicateHierarchyCodetable(codetableId, this.props.params.actionType,
      this.props.item?.descendantCount);

    return <div className="entity-view">
      <Paper componentId="code-base-paper" className={undefined}>
        <OverlayComponent
          displayOverlay={!displayMergeCodeModal && (savingCodetableItem || deletingCodeItem)}
          overlayText={overlayText} />

        {this.props.shouldDisplayModal
          ? <PromptConfirmation
            title={translation.cancel_changes_title}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.props.continue}
            onCancel={this.props.cancel}
          />
          : null}

        {displayMergeCodeModal
          ? <MergeCodeModal
            mergeCode={{
              ...this.props.item.mergeCode,
              codetableId: codetableMetadata?.codetable?.id,
              codeId: codeItem.id,
              codeDescription: this.getCodeItemDisplayedName(),
              replaceInfoText: codetableMetadata?.label
            }}

            breadcrumbs={this.props.breadcrumbs}
            metadata={codetableMetadata}
            options={this.props.item.codetableOptions}
            canDelete={access.canDelete}
            fetching={deletingCodeItem}
            translation={translation}
            validation={validation}

            deleteCodeItem={this.deleteCodeItem}
            onClose={this.mergeCodeItem}
            updateMergeCode={this.props.updateMergeCode}
            loadOptions={this.props.loadCodetableOptions}
            resetValidation={this.props.resetValidation}
            updateValidation={this.props.updateValidation} />
          : null}

        {this.props.item.showDuplicateHierarchy
          ? <DuplicateHierarchyModal
            codeId={codeItem.id}
            codeDescription={this.getCodeItemDisplayedName(true)}
            fetching={fetchingCodeId}
            translation={translation}
            onClose={this.props.toggleDuplicateHierarchy}
            onDone={this.save} />
          : null}

        <CodetableStickyHeader
          save={this.save}
          shouldPrompt={this.props.shouldPrompt}
          cancel={this.cancel}
          codeItem={codeItem}
          canDelete={access.canDelete}
          canMerge={codetableMetadata.merge}
          canDuplicateHierarchy={canDuplicateHierarchy}
          toggleDuplicateHierarchy={this.props.toggleDuplicateHierarchy}
          deleteCodeItem={this.props.deleteCodeItem}
          duplicateCodeItem={access.canDuplicate && this.duplicateCodeItem}
          mergeCodeItem={this.mergeCodeItem}
          codeDescription={this.getCodeItemDisplayedName()}
          originalCodeDescription={this.getCodeItemDisplayedName(true)}
          codetableName={codetableMetadata.label}
          codetableId={codetableId}
          codeId={this.props.codeId}
          translation={translation}
          currentLongDescription={currentLongDescription}
          originalLongDescription={originalLongDescription}
          disabled={codeItemDisabled}
          readOnlyConfig={readOnlyConfig}
          breadcrumbs={breadcrumbs}
          actionType={actionType}
        />
        <CodeScreenRenderer
          {...this.props}
          {...this.props.item}
          {...this.props.sharedState}
          invalidSection={this.state.invalidSection}
          supportedScreens={supportedScreens}
          codetableId={codetableId}
          readOnlyConfig={readOnlyConfig}
          hasBigStickyHeader={!!currentLongDescription}
          configuration={configuration}
          scrollViewUrl={scrollViewUrl}
          initialSection={initialSection}
          metadata={codetableMetadata}
          actionType={actionType}
          hasTranslationsToggled={this.state.showTranslations}
          switchTranslationsToggle={this.switchTranslationsToggle}
        />
      </Paper>
    </div>;
  }
}

const selectors = { ...commonSelectors, ...codeItemSelectors };

const container = containerFactory(
  withOptions(withPrompt(CodetableItemComponent) as any),
  actionCreators,
  selectors,
  codetableAdministrationTranslation
);

export { container as CodetableItem };
